import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@shared/hooks/useAuth';
import { CatLogo } from '@shared/components/CatLogo';
import OceanScene from '@shared/components/shared/OceanScene';
import Jellyfish from '@/effects/animals/Jellyfish';
import { LogIn, UserPlus, Mail, Lock, Sparkles, ArrowLeft, ShieldCheck, Eye, EyeOff, Loader2 } from 'lucide-react';

// 本地微信 OAuth 弹出登录地址（保留向后兼容）
const WECHAT_AUTHORIZE_URL = 'https://localhost.weixin.qq.com:14013/api/authorize';
const WECHAT_CHECK_LOGIN_URL = 'https://localhost.weixin.qq.com:14013/api/check-login';
const BACKEND_USER_URL = '/api/auth/me';

const Login = () => {
  const navigate = useNavigate();
  const { login, signup, verifyOtp, sendOtp, resetPassword } = useAuth();

  const [view, setView] = useState('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [authMode, setAuthMode] = useState('password');
  const [step, setStep] = useState('auth');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const wechatPollRef = useRef(null);
  const wechatPopupRef = useRef(null);

  useEffect(() => {
    return () => {
      if (wechatPollRef.current) clearInterval(wechatPollRef.current);
      if (wechatPopupRef.current && !wechatPopupRef.current.closed) wechatPopupRef.current.close();
    };
  }, []);

  const handleNavigateHome = () => {
    setLoginSuccess(true);
    setTimeout(() => navigate('/', { replace: true }), 1200);
  };

  const handleSendOtp = async () => {
    if (!email) { setError('请输入邮箱地址'); return; }
    setOtpSending(true); setError(''); setSuccess(false);
    try {
      await sendOtp(email);
      setSuccess(true);
      setError('验证码已发送至您的邮箱');
    } catch (err) {
      setError(err.message || '发送验证码失败');
    } finally {
      setOtpSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(false); setLoading(true);
    try {
      if (isSignup) {
        await signup(email, password);
        await sendOtp(email);
        setSuccess(true); setStep('verify');
        setError('验证码已发送至您的邮箱，请输入6位验证码完成注册');
      } else if (authMode === 'otp') {
        const result = await verifyOtp(email, otp, 'email');
        if (result?.user) handleNavigateHome();
      } else {
        await login(email, password);
        handleNavigateHome();
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Email not confirmed') || msg.includes('not confirmed') || msg.includes('邮箱未确认')) {
        try {
          await sendOtp(email);
          setSuccess(true); setStep('verify');
          setError('邮箱未确认，请输入6位验证码完成验证');
        } catch (otpErr) {
          setError(otpErr.message || '发送验证码失败');
        }
      } else {
        setError(msg || '操作失败，请重试');
      }
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await verifyOtp(email, otp, 'email');
      setError('验证成功！即将跳转到首页...');
      handleNavigateHome();
    } catch (err) { setError(err.message || '验证码错误，请重试'); }
    finally { setLoading(false); }
  };

  const handleWechatLogin = async () => {
    setWechatLoading(true); setError('');
    try {
      const popupWidth = 700, popupHeight = 600;
      const left = (window.screen.width - popupWidth) / 2;
      const top = (window.screen.height - popupHeight) / 2;
      const popup = window.open(
        WECHAT_AUTHORIZE_URL,
        'wechat_login',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes`
      );
      wechatPopupRef.current = popup;
      if (!popup || popup.closed) { setError('弹窗被拦截，请允许浏览器弹窗后重试'); setWechatLoading(false); return; }

      let retryCount = 0;
      const maxRetries = 120;
      const result = await new Promise((resolve, reject) => {
        const pollTimer = setInterval(async () => {
          retryCount++;
          try {
            const resp = await fetch(WECHAT_CHECK_LOGIN_URL, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
            const data = await resp.json();
            if (data && (data.logged_in === true || data.success === true || data.code === 0)) {
              clearInterval(pollTimer);
              if (popup && !popup.closed) popup.close();
              resolve(data);
            }
          } catch (pollErr) { /* silent */ }
          if (popup && popup.closed) { clearInterval(pollTimer); reject(new Error('用户取消了微信登录')); }
          if (retryCount >= maxRetries) { clearInterval(pollTimer); if (popup && !popup.closed) popup.close(); reject(new Error('微信登录超时，请重试')); }
        }, 2000);
        wechatPollRef.current = pollTimer;
      });

      const token = result?.token || result?.access_token || '';
      if (token) {
        localStorage.setItem('access_token', token);
        localStorage.setItem('refresh_token', result?.refresh_token || result?.refreshToken || '');
      }
      handleNavigateHome();
    } catch (err) {
      setError(err.message || '微信登录失败');
      if (wechatPollRef.current) { clearInterval(wechatPollRef.current); wechatPollRef.current = null; }
    } finally { setWechatLoading(false); }
  };

  const resetAuth = () => { setStep('auth'); setOtp(''); setError(''); setIsSignup(false); setSuccess(false); setAuthMode('password'); };

  return (
    <div className="fixed inset-0 overflow-hidden" style={{ background: 'rgba(6, 18, 34, 0.75)' }}>

      {/* 登录成功过渡 */}
      {loginSuccess && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ animation: 'login-success-fade 1s ease both', background: 'radial-gradient(circle at center, rgba(88,183,232,0.15) 0%, rgba(6,18,34,0.95) 70%)' }}>
          <div className="text-center" style={{ animation: 'login-success-zoom 0.8s cubic-bezier(0.22, 0.61, 0.36, 1) 0.2s both' }}>
            <Sparkles className="w-12 h-12 mx-auto mb-4" style={{ color: '#f2b94b' }} />
            <p className="text-lg" style={{ color: '#eef3f7' }}>欢迎回来...</p>
          </div>
        </div>
      )}

      {/* 分栏布局 */}
      <div className="relative w-full h-full flex">

        {/* 左侧 55%: 海洋场景 */}
        <div className="hidden lg:block relative h-full" style={{ width: '55%' }}>
          <OceanScene showBrandText={true} rightGradient={true} />
        </div>

        {/* 右侧 45%: 登录面板 — 透明叠加层 + 水母 */}
        <div className="relative h-full flex flex-col items-center justify-center px-6 md:px-10 lg:px-12 overflow-y-auto"
          style={{ width: '100%', maxWidth: '100%', background: 'rgba(8, 18, 32, 0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>

          {/* 登录面板内部 — 动态水母叠加层 */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
            <div style={{ position: 'absolute', top: '8%', left: '8%', opacity: 0.5 }}>
              <Jellyfish size={70} delay={0} duration={12} opacity={0.6} />
            </div>
            <div style={{ position: 'absolute', bottom: '12%', right: '10%', opacity: 0.4 }}>
              <Jellyfish size={55} delay={3} duration={14} opacity={0.5} />
            </div>
            <div style={{ position: 'absolute', top: '45%', right: '25%', opacity: 0.3 }}>
              <Jellyfish size={40} delay={6} duration={16} opacity={0.4} />
            </div>
          </div>

          {/* 移动端顶部 Logo */}
          <div className="lg:hidden absolute top-6 left-1/2 -translate-x-1/2 z-10">
            <CatLogo size="md" />
          </div>

          {/* 桌面端返回首页 */}
          <div className="hidden lg:flex absolute top-6 right-8 z-10 items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-xs transition px-3 py-1.5 rounded-lg"
              style={{ color: '#8898ab', border: '1px solid rgba(156,200,224,0.15)' }}
            >
              返回首页
            </button>
          </div>

          {/* 登录表单 */}
          <div className="w-full max-w-[400px]" style={{ animation: 'login-panel-reveal 1s cubic-bezier(0.22, 0.61, 0.36, 1) 1.8s both' }}>
            <div className="hidden lg:flex items-center justify-center mb-8">
              <CatLogo size="lg" />
            </div>

            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold cb">{isSignup ? '创建账号' : '欢迎回来'}</h1>
              <p className="text-sm cm mt-2">
                {isSignup ? '注册后即可开始规划你的旅程' : '登录后继续你的旅行计划'}
              </p>
            </div>

            {step === 'auth' && !isSignup && (
              <>
                <button
                  type="button"
                  onClick={handleWechatLogin}
                  disabled={wechatLoading}
                  className="w-full py-3 rounded-xl flex items-center justify-center gap-2 text-white font-medium mb-5 transition hover:opacity-90 disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #07C160, #00A651)', boxShadow: '0 4px 14px rgba(7,193,96,0.3)' }}
                >
                  {wechatLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8.5 11a1 1 0 110-2 1 1 0 010 2zm4 0a1 1 0 110-2 1 1 0 010 2zm-7.5-4.5C5 4.5 7 3 9.5 3c2.09 0 3.87 1.14 4.45 2.74A6.94 6.94 0 0116 5.5c3.31 0 6 2.24 6 5 0 1.62-.91 3.06-2.35 4l.8 2.65-2.8-1.55c-.55.17-1.13.25-1.7.25-.35 0-.69-.03-1.02-.08A9.7 9.7 0 0111.5 16c-3.58 0-6.5-2.46-6.5-5.5 0-.5.07-.98.2-1.44zm6.5 9.5a6.5 6.5 0 100-13 6.5 6.5 0 0 0 0 13z"/></svg>
                  )}
                  {wechatLoading ? '微信登录中...' : '微信登录'}
                </button>

                <div className="ocean-divider my-5">
                  <span className="cd text-xs px-3">或使用邮箱</span>
                </div>

                <div className="flex rounded-xl p-1 mb-5" style={{ background: 'rgba(22,37,68,0.5)' }}>
                  <button
                    type="button"
                    onClick={() => setAuthMode('password')}
                    className="flex-1 py-2 text-sm rounded-lg transition"
                    style={authMode === 'password' ? { background: 'rgba(106,179,214,0.2)', color: '#eef3f7' } : { color: '#8898ab' }}
                  >
                    密码登录
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMode('otp')}
                    className="flex-1 py-2 text-sm rounded-lg transition"
                    style={authMode === 'otp' ? { background: 'rgba(106,179,214,0.2)', color: '#eef3f7' } : { color: '#8898ab' }}
                  >
                    验证码登录
                  </button>
                </div>
              </>
            )}

            {step === 'verify' ? (
              <form onSubmit={handleVerify} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium cb">6位邮箱验证码</label>
                  <div className="relative">
                    <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 cd" />
                    <input type="text" inputMode="numeric" maxLength={6} placeholder="请输入6位验证码" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required autoFocus className="ocean-input pl-11 text-center tracking-[0.6em] text-lg font-semibold" />
                  </div>
                </div>
                {error && <div className={`p-3 rounded-xl text-sm ${success ? 'bg-green-900/30 text-green-300 border border-green-700/40' : 'bg-red-900/30 text-red-300 border border-red-700/40'}`}>{error}</div>}
                <button type="submit" disabled={loading || otp.length !== 6} className="btl w-full bt py-3.5">
                  {loading ? <span className="flex items-center gap-2 justify-center"><Sparkles className="w-4 h-4 animate-spin" />验证中...</span> : <span className="flex items-center gap-2 justify-center"><ShieldCheck className="w-4 h-4" />确认验证</span>}
                </button>
                <button type="button" onClick={resetAuth} className="w-full flex items-center justify-center gap-1 text-sm cd hover:!cb transition">
                  <ArrowLeft className="w-4 h-4" /> 返回登录
                </button>
              </form>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium cb">邮箱地址</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 cd" />
                      <input type="email" placeholder="example@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="ocean-input pl-11" />
                    </div>
                  </div>

                  {(authMode === 'password' || isSignup) ? (
                    <div className="space-y-2">
                      <label className="text-sm font-medium cb">{isSignup ? '设置密码' : '密码'}</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 cd" />
                        <input type={showPwd ? 'text' : 'password'} placeholder={isSignup ? '请设置密码' : '请输入密码'} value={password} onChange={(e) => setPassword(e.target.value)} required className="ocean-input pl-11 pr-11" />
                        <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 cd hover:!cb transition">
                          {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-sm font-medium cb">邮箱验证码</label>
                      <div className="relative flex gap-3">
                        <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 cd" />
                        <input type="text" inputMode="numeric" maxLength={6} placeholder="请输入6位验证码" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required className="ocean-input pl-11 flex-1" />
                        <button type="button" onClick={handleSendOtp} disabled={otpSending} className="bt text-xs px-4 whitespace-nowrap shrink-0">
                          {otpSending ? '发送中...' : '获取验证码'}
                        </button>
                      </div>
                    </div>
                  )}

                  {error && <div className={`p-3 rounded-xl text-sm ${success ? 'bg-green-900/30 text-green-300 border border-green-700/40' : 'bg-red-900/30 text-red-300 border border-red-700/40'}`}>{error}</div>}

                  <button type="submit" disabled={loading} className="btl w-full bt py-3.5">
                    {loading ? (
                      <span className="flex items-center gap-2 justify-center"><Sparkles className="w-4 h-4 animate-spin" />处理中...</span>
                    ) : isSignup ? (
                      <span className="flex items-center gap-2 justify-center"><UserPlus className="w-4 h-4" />注册账号</span>
                    ) : (
                      <span className="flex items-center gap-2 justify-center"><LogIn className="w-4 h-4" />登录</span>
                    )}
                  </button>
                </form>

                {!isSignup ? (
                  <div className="mt-5 flex items-center justify-between text-sm">
                    <button type="button" onClick={async () => { if (!email) { setError('请输入邮箱地址'); return; } setLoading(true); try { await resetPassword(email); setSuccess(true); setError('重置链接已发送至您的邮箱'); } catch (err) { setError(err.message); } finally { setLoading(false); } }} className="cd hover:!cb transition">忘记密码？</button>
                    <button type="button" onClick={() => { setIsSignup(true); setError(''); setSuccess(false); }} className="ca hover:!cg transition">没有账号？去注册</button>
                  </div>
                ) : (
                  <div className="mt-5 text-center">
                    <button type="button" onClick={() => { setIsSignup(false); setError(''); setSuccess(false); }} className="text-sm ca hover:!cg transition">已有账号？去登录</button>
                  </div>
                )}
              </>
            )}
          </div>

          <p className="text-center text-[11px] cd mt-6 tracking-wide absolute bottom-4">
            © 2026 西蒙的猫 Simon's Cat Travel
          </p>
        </div>
      </div>

      {/* 移动端背景 */}
      <div className="lg:hidden absolute inset-0 z-0 opacity-40">
        <img
          src="/bg-deep-sea.png"
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: 'saturate(0.4) brightness(0.4) contrast(1.1)' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(6,18,34,0.7) 0%, rgba(6,18,34,0.45) 30%, rgba(6,18,34,0.85) 100%)' }} />
      </div>

      <style>{`
        @keyframes login-panel-reveal {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes login-success-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes login-success-zoom {
          from { opacity: 0; transform: scale(0.85); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Login;
